/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
/** 
 * Salesforce Service Cloud Voice Demo Connector
 * @author dlouvton
 */

/** @module connector **/
import { Constants, VendorConnector, TelephonyConnector } from '@salesforce/scv-connector-base';
import { Sdk } from './vendor-sdk';
import {hidDeviceHandler} from "../hid/hidDeviceHandler";

/** 
 * Class representing a Service Cloud Voice Demo Telephony Connector
 */
export class PhoneConnector extends TelephonyConnector {
    /**
     * Create a Telephony Connector instance.
     * @param {object} sdk - Telephony SDK
     */
    constructor(sdk) {
        super();
        this.sdk = sdk;
    }
    /**
     * Called when the connector is loaded, to request the active calls
     */
    getActiveCalls() {
        return this.sdk.getActiveCalls();
    }
    /**
     * Called when call is accepted on the omni widget
     * @param {PhoneCall} call
     */
    acceptCall(call) {
        return this.sdk.acceptCall(call);
    }
    /**
     * Called when call is declined
     * @param {PhoneCall} call
     */
    declineCall(call) {
        // TODO: Update core to pass call on declineCall
        return this.sdk.declineCall(call ? call : { callAttributes: { participantType: Constants.PARTICIPANT_TYPE.INITIAL_CALLER }});
    }
    /**
     * Called when agent hangs up or when a participant (customer or third party) is
     * removed by the agent.
     * @param {PhoneCall} call
     * @param {string} agentStatus
     */
    endCall(call, agentStatus) {
        return this.sdk.endCall(call, agentStatus);
    }
    /**
     * Called when call is muted from the sfdc call controls
     * @param {PhoneCall} call
     */
    mute(call) {
        return this.sdk.mute(call);
    }
    /**
     * Called when call is unmuted from the sfdc call controls
     * @param {PhoneCall} call
     */
    unmute(call) {
        return this.sdk.unmute(call)
    }
    /**
     * Called when customer/third party call is put on hold by the agent
     * @param {PhoneCall} call call
     */
    hold(call) {
        return this.sdk.hold(call)
    }
    /**
     * Called when call is resumed (off hold for either customer/third party) from
     * the sfdc call controls
     * @param {PhoneCall} call call
     */
    resume(call) {
        return this.sdk.resume(call);
    }
    /**
     * Called when recording is paused from the sfdc call controls
     * @param {PhoneCall} call
     */
    pauseRecording(call) {
        return this.sdk.pauseRecording(call);
    }
    /**
     * Called when recording is resumed from the sfdc call controls
     * @param {PhoneCall} call
     */
    resumeRecording(call) {
        return this.sdk.resumeRecording(call);
    }
    /**
     * Called when participants on a call are swapped
     * @param {PhoneCall} call1 first call to be swapped
     * @param {PhoneCall} call2 second call to be swapped
     */
    swap(call1, call2) {
        return this.sdk.swapCalls(call1, call2);
    }
    /**
     * Called when participants are joined for a conference
     * @param {PhoneCall[]} calls
     */
    conference(calls) {
        return this.sdk.conference(calls);
    }
    /**
     * Called when an outbound call is made 
     * @param {Contact} contact
     * @param {DialOptions} dialOptions
     */
    dial(contact, dialOptions) {
        return this.sdk.dial(contact, {}, false, dialOptions && dialOptions.isCallback, dialOptions && dialOptions.isConsultCall);
    }
    /**
     * Called when an agent sends digits on the existing call @digits: a string of
     * digits to send to the existing connected call.
     * @param {string} digits digits
     */
    sendDigits(digits) {
        return this.sdk.sendDigits(digits);
    }
    /**
     * Called when speed dial is clicked in order to request the vendor to get the agent phone contacts
     * @param {Object} filter
     */
    getPhoneContacts(filter) {
        return this.sdk.getPhoneContacts(filter);
    }
    /**
     * add participant to the call through either an address or a free form Phone Number.
     * @param {Contact} contact
     * @param {PhoneCall} call
     * @param {Boolean} isBlindTransfer
     */
    addParticipant(contact, call, isBlindTransfer) {
        return this.sdk.addParticipant(contact, call, isBlindTransfer);
    }
    /**
     * Called when connector is ready to get the agent configuration
     */
    getAgentConfig() {
        return this.sdk.getAgentConfig();
    }

    /**
    * Used to set the agent config, including the selected phone type and number
    */
    setAgentConfig(config) {
        //pass HID device information selected by an Agent and call the handler
        if(config.hidDeviceInfo !== undefined) {
            //TODO: Currently just getting called from here, if need arises we can expose it as a separate method
            hidDeviceHandler(config, this.sdk);
        }
        return this.sdk.setAgentConfig(config);
    }
     /**
     * Called when connector is ready to get the voice vendor or agent capabilities
     */
    getVoiceCapabilities() {
        return this.sdk.getVoiceCapabilities();
    }
    /**
    * Used to set the vendor or agent capabilities
    */
    setCapabilities(capabilities) {
        return this.sdk.setCapabilities(capabilities);
    }

    /**
    * Used to finish wrap-up
    */
    wrapUpCall() {
        this.sdk.endWrapup();
    }

     /**
     * Get the signed recording url
     * @param {String} recordingUrl
     * @param {String} vendorCallKey
     * @param {String} callId
     * @returns {Promise<SignedRecordingUrlResult>} 
     */
    getSignedRecordingUrl(recordingUrl, vendorCallKey, callId) {
        return this.sdk.getSignedRecordingUrl(recordingUrl, vendorCallKey, callId);
    }

    superviseCall(parentCall){
        console.log("superviseCall", parentCall);
        return this.sdk.superviseCall(parentCall);  
    }

    supervisorDisconnect(parentCall){
        console.log("supervisorDisconnect", parentCall); 
        return this.sdk.supervisorDisconnect(parentCall);
    }

    supervisorBargeIn(parentCall){
        console.log("supervisorBargeIn", parentCall); 
        return this.sdk.supervisorBargeIn(parentCall);
    }
}


/** 
 * Class representing a Service Cloud Voice Demo Common Connector 
 */
export class Connector extends VendorConnector {
    /**
     * Create a Connector instance.
     * @param {object} state - Vendor SDK state
     */
    constructor(state) {
        super();
        this.sdk = new Sdk(state);
        this.telephonyConnector = new PhoneConnector(this.sdk);
    }
    /**
     * Returns the Telephony Connector (if implemented)
     */
    getTelephonyConnector() {
        return this.telephonyConnector;
    }
    /**
     * Called by SFDC to initialize the connector
     * @param {object} callCenterConfig - SFDC Contact Center Settings
     */
    init(callCenterConfig) {
        return this.sdk.init(callCenterConfig);
    }

    /**
     * Called when agent sets their status/presence (i.e. when changing from
     * Available to Offline) 
     * @param {string} agentStatus agent status, Constants.AGENT_STATUS.ONLINE or Constants.AGENT_STATUS.OFFLINE
     * @param {AgentStatusInfo} agentStatusInfo object contains statusId, statusApiName and statusName
     * @param {boolean} enqueueNextState true if the state should be enqueued, which will update the agent's status after a call ends
     */
    setAgentStatus(agentStatus, agentStatusInfo, enqueueNextState) {
        return this.sdk.setAgentStatus(agentStatus, agentStatusInfo, enqueueNextState)
    }
    /**
     * Sends non-voice agent work events to vendor such as work accepted, declined, etc
     * @param {AgentWork} agentWork
     * 
     */
    onAgentWorkEvent(agentWork) {
        return this.sdk.onAgentWorkEvent(agentWork);
    }
    /**
     * logout from the telephony system.
     */
    logout() {
        return this.sdk.omniLogout();
    }
    
    /**
    * Delegate Message received from sfdc component to sdk
    * @param {object} message - Message
    */
    handleMessage(message) {
        return this.sdk.handleMessage(message);
    }
    /**
     * Called when transfer is clicked in order to request the vendor to get the agent contacts
     * @param {Object} filter
     * @param {string} workItemId
     */
    getContacts(filter, workItemId) {
        return this.sdk.getContacts(filter, workItemId);
    }
        /**
     * Called when connector is ready to get the shared vendor or agent capabilities
     */
    getSharedCapabilities() {
        return this.sdk.getSharedCapabilities();
    }
}
